import React, { useState, useEffect, useCallback } from 'react';
import { useAppContext } from '../../context/AppContext';
import { 
  Users, Shield, CheckCircle, XCircle, 
  Settings, UserCheck, Lock,
  Edit3, Plus, Trash2, Search, Save, X, ChevronDown, ChevronUp
} from 'lucide-react';
import RoleService from '../../services/api/RoleService';
import { canPerformAction } from '../../utils/helpers';

const RoleManagement = () => {
  const { state } = useAppContext();
  const { currentUser, deviceInfo } = state;
  const { optimization } = deviceInfo;

  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPermission, setFilterPermission] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', permissions: [], tabs: [] });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [permissionSearch, setPermissionSearch] = useState('');
  const [tabSearch, setTabSearch] = useState('');
  const [expandedFormSection, setExpandedFormSection] = useState('permissions');

  const canManageRoles = canPerformAction(currentUser, 'manage_users');

  const allPermissions = RoleService.getAllPermissions();
  const allTabs = RoleService.getAllTabs();

  const loadRoles = useCallback(async () => {
    setLoading(true);
    try {
      const result = await RoleService.getAll();
      if (result.success) {
        setRoles(result.data);
        if (!selectedRole && result.data.length > 0) {
          setSelectedRole(result.data[0].id);
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedRole]);

  useEffect(() => { loadRoles(); }, [loadRoles]);

  const filteredRoles = roles.filter(role =>
    role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.slug.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedRoleData = roles.find(r => r.id === selectedRole);

  const handleOpenCreate = () => {
    setEditingRole(null);
    setFormData({ name: '', description: '', permissions: [], tabs: [] });
    setShowForm(true);
  };

  const handleOpenEdit = (role) => {
    setEditingRole(role.id);
    setFormData({
      name: role.name,
      description: role.description || '',
      permissions: [...(role.permissions || [])],
      tabs: [...(role.tabs || [])]
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Role name is required');
      return;
    }

    let result;
    if (editingRole) {
      result = await RoleService.update(editingRole, formData);
    } else {
      result = await RoleService.create(formData);
    }

    if (result.success) {
      setShowForm(false);
      setEditingRole(null);
      setError(null);
      await loadRoles();
      if (result.data) setSelectedRole(result.data.id);
    } else {
      setError(result.error);
    }
  };

  const handleDelete = async (roleId) => {
    const result = await RoleService.delete(roleId);
    if (result.success) {
      setDeleteConfirm(null);
      if (selectedRole === roleId) setSelectedRole(null);
      await loadRoles();
    } else {
      setError(result.error);
    }
  };

  const togglePermission = (perm) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter(p => p !== perm)
        : [...prev.permissions, perm]
    }));
  };

  const toggleTab = (tab) => {
    setFormData(prev => ({
      ...prev,
      tabs: prev.tabs.includes(tab)
        ? prev.tabs.filter(t => t !== tab)
        : [...prev.tabs, tab]
    }));
  };

  const renderRoleCard = (role) => {
    const isSelected = selectedRole === role.id;
    return (
      <div 
        key={role.id}
        onClick={() => setSelectedRole(role.id)}
        className={`cursor-pointer rounded-xl p-4 border-2 transition-all ${
          isSelected 
            ? 'border-purple-500 bg-purple-50 shadow-lg' 
            : 'border-gray-200 bg-white hover:border-purple-300 hover:shadow-md'
        }`}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isSelected ? 'bg-purple-100' : 'bg-gray-100'}`}>
              <Users className={`w-5 h-5 ${isSelected ? 'text-purple-600' : 'text-gray-600'}`} />
            </div>
            <div>
              <h3 className={`font-semibold ${isSelected ? 'text-purple-800' : 'text-gray-800'}`}>
                {role.name}
              </h3>
              <p className="text-sm text-gray-600">{role.slug}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {role.isSystem && (
              <span className="px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-600">System</span>
            )}
            <span className={`px-2 py-0.5 text-xs rounded-full ${
              (role.permissions || []).length > 10 ? 'bg-green-100 text-green-800' 
              : (role.permissions || []).length > 5 ? 'bg-yellow-100 text-yellow-800'
              : 'bg-blue-100 text-blue-800'
            }`}>
              {(role.permissions || []).length} perms
            </span>
          </div>
        </div>
        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{role.description || 'No description'}</p>
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <Settings className="w-3 h-3" /> {(role.tabs || []).length} modules
          </span>
          <div className="flex gap-1">
            <button onClick={(e) => { e.stopPropagation(); handleOpenEdit(role); }}
              className="p-1 hover:bg-purple-100 rounded transition-colors" title="Edit">
              <Edit3 className="w-3.5 h-3.5 text-purple-600" />
            </button>
            {!role.isSystem && (
              <button onClick={(e) => { e.stopPropagation(); setDeleteConfirm(role.id); }}
                className="p-1 hover:bg-red-100 rounded transition-colors" title="Delete">
                <Trash2 className="w-3.5 h-3.5 text-red-600" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderPermissionList = () => {
    if (!selectedRoleData) return null;
    const perms = (selectedRoleData.permissions || []).filter(p =>
      !filterPermission || p.toLowerCase().includes(filterPermission.toLowerCase())
    );

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">
            Permissions for {selectedRoleData.name}
          </h3>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" placeholder="Filter permissions..."
              value={filterPermission} onChange={(e) => setFilterPermission(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {perms.map((permission, i) => (
            <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-gray-800">{permission.replace(/_/g, ' ')}</span>
              </div>
              <div className="flex items-center gap-2">
                {permission.includes('manage') && <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800">Admin</span>}
                {permission.includes('view') && <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800">Read</span>}
                {permission.includes('edit') && <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-800">Write</span>}
              </div>
            </div>
          ))}
          {perms.length === 0 && <p className="text-gray-500 text-sm col-span-2">No permissions match the filter.</p>}
        </div>
      </div>
    );
  };

  const renderModuleAccess = () => {
    if (!selectedRoleData) return null;
    const allModules = [
      'dashboard', 'residents', 'enquiries', 'clinical', 'medication',
      'careplans', 'activities', 'quality', 'roster', 'billing',
      'reports', 'network', 'settings'
    ];

    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Module Access</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {allModules.map(mod => {
            const hasAccess = (selectedRoleData.tabs || []).includes(mod);
            return (
              <div key={mod} className={`p-3 rounded-lg border-2 ${hasAccess ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-medium capitalize ${hasAccess ? 'text-green-800' : 'text-gray-500'}`}>
                    {mod.replace('_', ' ')}
                  </span>
                  {hasAccess ? <CheckCircle className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-gray-400" />}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderForm = () => {
    const filteredPerms = allPermissions.filter(p => !permissionSearch || p.toLowerCase().includes(permissionSearch.toLowerCase()));
    const filteredTabs = allTabs.filter(t => !tabSearch || t.toLowerCase().includes(tabSearch.toLowerCase()));

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <div className="p-6 border-b bg-gradient-to-r from-purple-600 to-pink-600 text-white">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">{editingRole ? 'Edit Role' : 'Create New Role'}</h2>
              <button onClick={() => { setShowForm(false); setError(null); }} className="p-1 hover:bg-white/20 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center justify-between">
                {error}
                <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700"><X className="w-4 h-4" /></button>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role Name *</label>
                <input type="text" value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Allied Health Professional"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={editingRole && roles.find(r => r.id === editingRole)?.isSystem} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={formData.description} onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                  rows={2} placeholder="Describe this role's responsibilities"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
              </div>
            </div>

            {/* Permissions Section */}
            <div className="border rounded-xl overflow-hidden">
              <button onClick={() => setExpandedFormSection(s => s === 'permissions' ? '' : 'permissions')}
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-purple-600" />
                  <span className="font-semibold">Permissions</span>
                  <span className="text-sm text-gray-500">({formData.permissions.length} selected)</span>
                </div>
                {expandedFormSection === 'permissions' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {expandedFormSection === 'permissions' && (
                <div className="p-4 space-y-3">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input type="text" placeholder="Search permissions..." value={permissionSearch}
                        onChange={e => setPermissionSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500" />
                    </div>
                    <button onClick={() => setFormData(p => ({ ...p, permissions: [...allPermissions] }))}
                      className="px-3 py-2 text-xs bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200">All</button>
                    <button onClick={() => setFormData(p => ({ ...p, permissions: [] }))}
                      className="px-3 py-2 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">None</button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                    {filteredPerms.map(perm => (
                      <label key={perm} className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer text-sm ${
                        formData.permissions.includes(perm) ? 'bg-purple-50 border border-purple-200' : 'bg-gray-50 border border-gray-200'
                      }`}>
                        <input type="checkbox" checked={formData.permissions.includes(perm)}
                          onChange={() => togglePermission(perm)}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500" />
                        <span className="truncate">{perm.replace(/_/g, ' ')}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Tabs/Modules Section */}
            <div className="border rounded-xl overflow-hidden">
              <button onClick={() => setExpandedFormSection(s => s === 'tabs' ? '' : 'tabs')}
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors">
                <div className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold">Module Access</span>
                  <span className="text-sm text-gray-500">({formData.tabs.length} selected)</span>
                </div>
                {expandedFormSection === 'tabs' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {expandedFormSection === 'tabs' && (
                <div className="p-4 space-y-3">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input type="text" placeholder="Search modules..." value={tabSearch}
                        onChange={e => setTabSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
                    </div>
                    <button onClick={() => setFormData(p => ({ ...p, tabs: [...allTabs] }))}
                      className="px-3 py-2 text-xs bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200">All</button>
                    <button onClick={() => setFormData(p => ({ ...p, tabs: [] }))}
                      className="px-3 py-2 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">None</button>
                  </div>
                  <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                    {filteredTabs.map(tab => (
                      <label key={tab} className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer text-sm capitalize ${
                        formData.tabs.includes(tab) ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'
                      }`}>
                        <input type="checkbox" checked={formData.tabs.includes(tab)}
                          onChange={() => toggleTab(tab)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                        <span className="truncate">{tab.replace(/_/g, ' ')}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
            <button onClick={() => { setShowForm(false); setError(null); }}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 flex items-center gap-2">
              <X className="w-4 h-4" /> Cancel
            </button>
            <button onClick={handleSave}
              className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 font-medium">
              <Save className="w-4 h-4" /> {editingRole ? 'Update Role' : 'Create Role'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!canManageRoles) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-red-100 rounded-lg">
            <Lock className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-red-800">Access Denied</h2>
            <p className="text-gray-600">You don't have permission to manage roles and permissions.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex justify-between items-center">
          <div>
            <h1 className={`${optimization.fontSize === 'text-sm' ? 'text-2xl' : 'text-3xl'} font-bold flex items-center gap-3`}>
              Role Management
              <Shield className={`${optimization.iconSize} text-purple-300`} />
            </h1>
            <p className={`${optimization.fontSize === 'text-sm' ? 'text-sm' : 'text-base'} mt-2 opacity-90`}>
              Manage user roles, permissions, and access levels ({roles.length} roles configured)
            </p>
          </div>
          <button onClick={handleOpenCreate}
            className={`bg-white text-purple-600 ${optimization.buttonSize} rounded-xl font-semibold hover:bg-opacity-90 ${optimization.animationSpeed} flex items-center gap-2`}>
            <Plus className={optimization.iconSize} /> Add Role
          </button>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mx-auto mb-4" />
          <p className="text-gray-500">Loading roles...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Role List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <h2 className="text-lg font-semibold">Available Roles</h2>
              </div>
              <div className="mb-4 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" placeholder="Search roles..." value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" />
              </div>
              <div className="space-y-3">
                {filteredRoles.map(renderRoleCard)}
                {filteredRoles.length === 0 && <p className="text-gray-500 text-sm text-center py-4">No roles match your search.</p>}
              </div>
            </div>
          </div>

          {/* Role Details */}
          <div className="lg:col-span-2">
            {selectedRoleData ? (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <UserCheck className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold">{selectedRoleData.name}</h2>
                      <p className="text-sm text-gray-600">{selectedRoleData.description || 'No description'}</p>
                    </div>
                  </div>
                  <button onClick={() => handleOpenEdit(selectedRoleData)}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 text-sm font-medium">
                    <Edit3 className="w-4 h-4" /> Edit
                  </button>
                </div>
                <div className="space-y-6">
                  {renderPermissionList()}
                  {renderModuleAccess()}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm p-12 text-center text-gray-500">
                <Shield className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Select a role to view its details.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {showForm && renderForm()}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-lg font-bold text-red-800 mb-2">Delete Role</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete the role <strong>{roles.find(r => r.id === deleteConfirm)?.name}</strong>?
              This cannot be undone. Users assigned this role will lose their access.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100">Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2">
                <Trash2 className="w-4 h-4" /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleManagement;
